import { Avatar, AvatarFallback, AvatarImage } from '@/components/avatar'
import { Button } from '@/components/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger
} from '@/components/dropdown-menu'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/form'
import { Input } from '@/components/input'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from '@/components/sidebar'
import { useSidebar } from '@/components/sidebar.context'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tooltip'
import sportableLogo from '@frontend/assets/images/sportable-logo-blue.svg'
import {
  DataTable,
  type DataTableColumnDef
} from '@frontend/components/enhanced/data-table'
import { RawOrganisationData } from '@frontend/core'
import {
  handleOrganisationSwitch,
  useOrganisations_createMutation,
  useOrganisations_updateMutation
} from '@frontend/core/lib/domains/organisations'
import { useWhoAmIQuery } from '@frontend/core/lib/modules/authentication'
import {
  useAppDispatch,
  useOrganisationFallback,
  useOrganisationsData,
  useSelectedOrganisation
} from '@frontend/react'
import { toast } from '@frontend/ui'
import {
  ChevronsUpDown,
  Loader,
  Plus,
  ShieldCheck,
  User,
  Users
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { requiredResolver } from '../../utils/form-resolver'

const formSchema = z.object({
  id: z.string().optional(),
  name: z.string()
})

export function OrganisationForm({
  onSubmit,
  initialValues
}: {
  onSubmit: (values: z.infer<typeof formSchema>) => void
  initialValues?: z.infer<typeof formSchema>
}) {
  const [updateOrganisation, updateResult] = useOrganisations_updateMutation()
  const [createOrganisation, createResult] = useOrganisations_createMutation()

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      if (values.id) {
        toast.promise(
          updateOrganisation({
            id: values.id,
            name: values.name
          }).unwrap(),
          {
            loading: 'Updating...',
            success: (data: RawOrganisationData) => {
              onSubmit(values)
              return `${data.name} has been updated`
            },
            error: (error) => {
              console.error('Update failed:', error)
              return 'Error'
            }
          }
        )
      } else {
        toast.promise(
          createOrganisation({
            name: values.name
          }).unwrap(),
          {
            loading: 'Creating...',
            success: (data: RawOrganisationData) => {
              onSubmit(values)
              return `${data.name} has been created`
            },
            error: 'Error'
          }
        )
      }
    } catch (err) {
      console.error('Update failed:', err)
    }
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: requiredResolver(formSchema),
    defaultValues: {
      name: ''
    },
    values: initialValues
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-8'>
        <FormField
          control={form.control}
          name='name'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organisation Name</FormLabel>
              <FormControl>
                <Input placeholder='' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className='flex justify-end'>
          <Button type='submit'>
            {' '}
            {form.getValues().id
              ? updateResult.isLoading
                ? 'Updating...'
                : 'Update'
              : createResult.isLoading
                ? 'Creating...'
                : 'Create'}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export function OrganisationTable({
  filteredData,
  isLoading,
  filters,
  updateFilterValue,
  showFilters = []
}: ReturnType<typeof useOrganisationsData> & {
  onTeamClick?: (teamId: string) => void
  showFilters?: (keyof ReturnType<typeof useOrganisationsData>['filters'])[]
}) {
  const navigate = useNavigate()

  const columns: DataTableColumnDef<RawOrganisationData>[] = [
    {
      id: 'img',
      header: '',
      cell: ({ row }) => {
        const organisation = row.original
        return (
          <div className='flex items-center justify-center gap-2'>
            {organisation.logo && (
              <img
                src={sportableLogo}
                alt={organisation.name}
                className='h-8 w-8 rounded-full object-cover object-top'
              />
            )}
          </div>
        )
      },
      enableSorting: false
    },
    {
      id: 'name',
      accessorFn: (row) => row.name,
      header: 'Name',
      cell: ({ row }) => {
        const organisation = row.original
        return (
          <div className='flex items-center gap-2'>
            <Button
              variant='link'
              className='text-foreground w-fit px-0 text-left'
              onClick={() => navigate(`/organisations/${row.original.id}`)}
            >
              {organisation.name}
            </Button>
          </div>
        )
      },
      enableSorting: true,
      enableHiding: true
    }
  ]

  return (
    <DataTable
      name='Organisation'
      columns={columns}
      data={filteredData}
      dataForm={OrganisationForm}
      isLoading={isLoading}
      enableCreate={false}
      filters={filters}
      updateFilterValue={updateFilterValue}
      showFilters={showFilters}
    />
  )
}

export function OrganisationSwitcher({ hideIfSingle = false }) {
  const dispatch = useAppDispatch()

  const {
    filteredData: organisations,
    options: organisationOptions,
    isLoading: isLoadingOrganisations
  } = useOrganisationsData({})

  const { organisation: selectedOrganisation, isLoading } =
    useSelectedOrganisation()

  useOrganisationFallback(organisations)

  // Hide switcher if only one organisation exists and hideIfSingle is true //
  if (
    hideIfSingle &&
    (organisationOptions.length < 2 || isLoadingOrganisations)
  ) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          className='w-auto justify-between gap-2 bg-transparent'
        >
          <div className='flex items-center gap-2'>
            {isLoading ? (
              <Loader className='text-background h-4 w-4 animate-spin' />
            ) : (
              <Avatar className='h-6 w-6 rounded-lg'>
                <AvatarImage
                  src={selectedOrganisation ? sportableLogo : ''}
                  alt={selectedOrganisation?.name || 'Organisation Logo'}
                />
                <AvatarFallback className='rounded-lg'>
                  {selectedOrganisation?.name?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
            )}
            {isLoading ? (
              <span className='truncate font-medium'>Loading...</span>
            ) : (
              <span className='truncate font-medium'>
                {selectedOrganisation?.name || 'No Organisation Found'}
              </span>
            )}
          </div>
          <ChevronsUpDown className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='min-w-[200px] rounded-lg' align='start'>
        <DropdownMenuLabel className='text-muted-foreground text-xs'>
          Organisations
        </DropdownMenuLabel>
        {organisationOptions.map((option, index) => (
          <DropdownMenuItem
            key={option.name}
            onClick={() => dispatch(handleOrganisationSwitch(option.value))}
            className='gap-2 p-2'
          >
            <Avatar className='h-6 w-6 rounded-lg'>
              <AvatarImage src={sportableLogo} alt={option.name} />
              <AvatarFallback className='rounded-lg'>
                {option.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            {option.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function SidebarOrganisationSwitcher() {
  const dispatch = useAppDispatch()

  const { isMobile } = useSidebar()

  const { filteredData: organisationsData } = useOrganisationsData({})
  const {
    organisation: selectedOrganisation,
    isLoading,
    isError
  } = useSelectedOrganisation()
  const { data: currentUser } = useWhoAmIQuery()

  const isSuperAdmin = currentUser?.globalRole === 'SportableSuperAdmin'

  const organisationOptions = useMemo(() => {
    return organisationsData.map((org) => ({
      name: org.name,
      logo: org.logo,
      value: org.id,
      sport: org.sport
    }))
  }, [organisationsData])

  const [openCreate, setOpenCreate] = useState(false)

  useOrganisationFallback(organisationsData)

  if (isError) {
    console.error('Error fetching organisations:', isError)
    return null
  }
  return (
    <SidebarMenu>
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Create Organisation</DialogTitle>
          </DialogHeader>
          <OrganisationForm onSubmit={() => setOpenCreate(false)} />
        </DialogContent>
      </Dialog>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <div className='flex aspect-square size-8 items-center justify-center rounded-lg'>
                {isLoading ? (
                  <Loader className='text-foreground h-4 w-4 animate-spin' />
                ) : (
                  <Avatar className='h-8 w-8 rounded-lg'>
                    <AvatarImage
                      src={selectedOrganisation ? sportableLogo : ''}
                      alt={selectedOrganisation?.name || 'Organisation Logo'}
                    />
                    <AvatarFallback className='rounded-lg'>
                      {selectedOrganisation?.name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                {isLoading ? (
                  <span className='truncate font-medium'>Loading...</span>
                ) : (
                  selectedOrganisation && (
                    <>
                      <span className='truncate font-medium'>
                        {selectedOrganisation?.name || 'No Organisation Found'}
                      </span>
                      <span className='text-muted-foreground flex items-center gap-2 truncate text-xs'>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='flex items-center gap-1'>
                              <Users className='size-3' />
                              {selectedOrganisation?.teamCount}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Teams</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='flex items-center gap-1'>
                              <ShieldCheck className='size-3' />
                              {selectedOrganisation?.adminCount}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Admins</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='flex items-center gap-1'>
                              <User className='size-3' />
                              {selectedOrganisation?.userCount}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Users</TooltipContent>
                        </Tooltip>
                      </span>
                    </>
                  )
                )}
              </div>
              <ChevronsUpDown className='ml-auto' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-(--radix-dropdown-menu-trigger-width) min-w-70 rounded-lg'
            align='start'
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className='text-muted-foreground text-xs'>
              Organisations
            </DropdownMenuLabel>
            {organisationOptions.map((option, index) => (
              <DropdownMenuItem
                key={option.name}
                onClick={() => dispatch(handleOrganisationSwitch(option.value))}
                className='gap-2 p-2'
              >
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarImage src={sportableLogo} alt={option.name} />
                  <AvatarFallback className='rounded-lg'>
                    {option.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                {/* <div className='flex size-6 items-center justify-center rounded-md border'></div> */}
                {option.name}
                <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
            {isSuperAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setOpenCreate(true)}
                  className='gap-2 p-2'
                >
                  <div className='flex size-6 items-center justify-center rounded-md border bg-transparent'>
                    <Plus className='size-4' />
                  </div>
                  <div className='text-muted-foreground font-medium'>
                    Add organisation
                  </div>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
